import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function QuestProgress() {
  const { questId } = useParams();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, inProgress, started

  useEffect(() => {
    fetchProgress();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchProgress, 10000);
    return () => clearInterval(interval);
  }, [questId]);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/linux-quest/${questId}/students-progress`);
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
      alert('Error al cargar el progreso');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredStudents = () => {
    if (!progress) return [];
    
    const students = progress.students;
    
    switch (filter) {
      case 'completed':
        return students.filter(s => s.completed);
      case 'inProgress':
        return students.filter(s => !s.completed && s.current_level > 1);
      case 'started':
        return students.filter(s => !s.completed && s.current_level === 1);
      default:
        return students;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completado':
        return 'bg-green-100 text-green-800';
      case 'En progreso':
        return 'bg-yellow-100 text-yellow-800';
      case 'Iniciado':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
          <p className="text-gray-600">Cargando progreso...</p>
        </div>
      </div>
    );
  }

  const filteredStudents = getFilteredStudents();
  const completedCount = progress?.students.filter(s => s.completed).length || 0;
  const inProgressCount = progress?.students.filter(s => !s.completed && s.current_level > 1).length || 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-800 mb-2 flex items-center gap-2"
              >
                ← Volver al dashboard
              </button>
              <h1 className="text-3xl font-bold text-gray-800">
                📊 Progreso: {progress?.quest_title}
              </h1>
              <p className="text-gray-600 mt-2">
                {progress?.total_levels} niveles • {progress?.total_students} estudiantes
              </p>
            </div>
            <button
              onClick={fetchProgress}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              🔄 Actualizar
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
              <p className="text-sm text-green-600 font-semibold">Completados</p>
              <p className="text-3xl font-bold text-green-700">{completedCount}</p>
            </div>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <p className="text-sm text-yellow-600 font-semibold">En Progreso</p>
              <p className="text-3xl font-bold text-yellow-700">{inProgressCount}</p>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <p className="text-sm text-blue-600 font-semibold">Total Estudiantes</p>
              <p className="text-3xl font-bold text-blue-700">{progress?.total_students}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === 'all'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos ({progress?.students.length})
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === 'completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ✅ Completados ({completedCount})
            </button>
            <button
              onClick={() => setFilter('inProgress')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === 'inProgress'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔄 En Progreso ({inProgressCount})
            </button>
            <button
              onClick={() => setFilter('started')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === 'started'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🚀 Iniciados ({progress?.students.filter(s => !s.completed && s.current_level === 1).length})
            </button>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {filteredStudents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                {filter === 'all' 
                  ? 'Ningún estudiante ha comenzado este quest todavía'
                  : 'No hay estudiantes en esta categoría'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estudiante
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nivel Actual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progreso
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha Inicio
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => (
                    <tr key={student.student_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                            <span className="text-green-600 font-semibold text-lg">
                              {student.student_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {student.student_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {student.student_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className="font-bold text-lg">{student.current_level}</span>
                          <span className="text-gray-500"> / {student.total_levels}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full ${
                              student.completed
                                ? 'bg-green-600'
                                : student.current_level > 1
                                ? 'bg-yellow-500'
                                : 'bg-blue-500'
                            }`}
                            style={{ width: `${student.progress_percent}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {student.progress_percent}%
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(student.status)}`}>
                          {student.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {student.started_at
                          ? new Date(student.started_at).toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Auto-refresh indicator */}
        <div className="text-center mt-4 text-sm text-gray-500">
          <p>🔄 Se actualiza automáticamente cada 10 segundos</p>
        </div>
      </div>
    </div>
  );
}

export default QuestProgress;
